import { useCallback, useEffect, useState } from "react";
import {
  fetchNotesByUserId,
  createNoteByUserId,
  updateNoteByNoteId,
  deleteNoteByNoteId,
} from "@campgladiator/cg-common.api.user";
import { NoteDTO } from "@campgladiator/cg-common.types.types";
import { NoteManagementProps } from "./";
import { ButtonProps } from "@campgladiator/cgui-core.atoms.button";

export type UseWithNoteProps = Pick<NoteManagementProps, "adminId"> & {
  userId: string;
  token: string;
};

const useWithNoteManagement = ({
  adminId,
  userId,
  token,
}: UseWithNoteProps) => {
  const [notes, setNotes] = useState<NoteDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>();
  const [successMessage, setSuccessMessage] = useState<string | undefined>(
    undefined
  );
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [pageSize] = useState<number>(40);
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [hasMoreNotes, setHasMoreNotes] = useState<boolean>(true);

  const getUniqueNotes = (prevNotes: NoteDTO[], newNotes: NoteDTO[]) => {
    return [...prevNotes, ...newNotes.slice(0, pageSize)].filter(
      (note, index, self) => index === self.findIndex((n) => n.id === note.id)
    );
  };

  const fetchNotes = useCallback(async () => {
    if (!userId) {
      const error = "User ID is missing. Not fetching notes.";
      console.error(error);
      setError(error);
      return;
    }

    setIsLoading(true);
    try {
      const { content, totalElements } = await fetchNotesByUserId(
        userId,
        token,
        page,
        pageSize,
        "DESC"
      );

      setNotes((prevNotes) => {
        const uniqueNotes = getUniqueNotes(prevNotes, content);
        return uniqueNotes;
      });

      if (totalElements <= getUniqueNotes(notes, content).length) {
        setHasMoreNotes(false);
      }

      setError(undefined);
      setIsFirstLoad(false);
    } catch (error) {
      console.error("Failed to fetch notes:", error);
      setError("Failed to fetch notes. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [userId, token, page, pageSize]);

  const handleAddNewNote = () => {
    if (isAdding) {
      setError("Please save or cancel the new note before adding another one.");
      return;
    }

    setIsAdding(true);
    const newNote = {
      id: Date.now().toString(),
      created: new Date().toISOString(),
      createdBy: { id: adminId },
      note: "",
      deleted: false,
      lastUpdatedBy: "",
      updated: "",
      userId: "",
    };
    setNotes((prevNotes) => [newNote, ...prevNotes]);
  };

  const handleCancelAddNewNote = async (id: string): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      try {
        setNotes((prevNotes) => prevNotes.filter((note) => note.id !== id));
        setError(undefined);
        resolve(true);
      } catch (error) {
        console.error("Failed to cancel new note:", error);
        setError("Failed to cancel new note. Please try again.");
        reject(false);
      } finally {
        setIsAdding(false);
      }
    });
  };

  const loadMoreNotes = () => {
    setPage((prevPage) => prevPage + 1);
  };

  const handleSaveNewNote = useCallback(
    async (id, note): Promise<boolean> => {
      const noteToSave = notes.find((note) => note.id === id);
      if (!noteToSave) {
        const error = `Note with id ${id} not found`;
        console.error(error);
        setError(error);
        return;
      }

      try {
        const payload = { note };
        const savedNote = await createNoteByUserId(userId, payload, token);
        setNotes((prevNotes) =>
          prevNotes.map((note) => (note.id === id ? savedNote : note))
        );
        setError(undefined);
        setSuccessMessage("Note saved successfully.");
        setIsAdding(false);
        return Promise.resolve(true);
      } catch (error) {
        console.error("Failed to save note:", error);
        setError("Failed to save new note. Please try again.");
        return Promise.resolve(false);
      }
    },
    [notes, userId]
  );

  const handleEditNote = useCallback(
    async (id, note): Promise<boolean> => {
      const noteToUpdate = notes.find((note) => note.id === id);
      if (!noteToUpdate) {
        const error = `Note with id ${id} not found`;
        console.error(error);
        setError(error);
        return;
      }

      try {
        const payload = { note };
        const updatedNote = await updateNoteByNoteId(id, payload, token);
        setNotes((prevNotes) =>
          prevNotes.map((note) => (note.id === id ? updatedNote : note))
        );
        setError(undefined);
        setSuccessMessage("Note updated successfully.");
        return Promise.resolve(true);
      } catch (error) {
        console.error("Failed to update note:", error);
        setError("Failed to edit note. Please try again.");
        return Promise.resolve(false);
      }
    },
    [notes, token]
  );

  const handleDeleteNote = useCallback(
    async (id): Promise<boolean> => {
      return new Promise(async (resolve, reject) => {
        try {
          await deleteNoteByNoteId(id, token);

          const fetchPromises = Array.from({ length: page + 1 }, (_, i) =>
            fetchNotesByUserId(userId, token, i, pageSize, "DESC")
          );
          const allPages = await Promise.all(fetchPromises);
          const allNotes = [].concat(...allPages.map(({ content }) => content));

          setNotes(allNotes);
          setError(undefined);
          setSuccessMessage("Note deleted successfully.");
          resolve(true);
        } catch (error) {
          console.error("Failed to delete note:", error);
          setError("Failed to delete note. Please try again.");
          reject(false);
        }
      });
    },
    [userId, token, notes.length, pageSize]
  );

  const clearErrors = () => {
    setError(undefined);
    setSuccessMessage(undefined);
  };

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const buttonProps: ButtonProps = {
    children: isLoading ? "Loading..." : "Load More",
    disabled: isLoading,
    emphasis: "secondary",
    size: "large",
    theme: "trainer",
    onClick: loadMoreNotes,
  };

  return {
    adminId,
    buttonProps,
    error,
    hasMoreNotes,
    isFirstLoad,
    isLoading,
    notes,
    successMessage,
    clearErrors,
    handleAddNewNote,
    handleCancelAddNewNote,
    handleDeleteNote,
    handleEditNote,
    handleSaveNewNote,
  };
};

export default useWithNoteManagement;
